import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { CliScope, UsageEntryFields, WebUsageTotals } from '../../types';
import { formatDateTimeUTC } from '../../utils/dateTime';
import CopyIconButton from '../CopyIconButton';
import { StatTile } from '../cards/StatCards';
import { Badge } from '../StatusBadge';
import { CliCost, costLabel, formatEstimateCents } from './cliCost';
import { formatCentsAsDollars } from '../../utils/money';
import {
    EmptyRow,
    SortableTh,
    TableCard,
    TableHeader,
    TableViewport,
    tdClass,
    thClass,
    usePageSize,
} from '../table/TableKit';
import { usePageClamp } from '../../hooks/usePageClamp';
import { urlEnum, urlPositiveInt, useUrlState } from '../../hooks/useUrlState';
import { useBackTo } from '../../hooks/useBackTo';

// One CLI operation, web job, or API request: the owner of a usage entries list.
export interface UsageSubject extends WebUsageTotals {
    id: string;
    operation: CliScope;
}

export interface UsageEntriesQuery {
    limit: number;
    offset: number;
    sort: string;
    order: 'asc' | 'desc';
}

export interface UsageDetailPage<T extends UsageSubject> {
    subject: T | null;
    entries: UsageEntryFields[];
    total: number;
}

interface UsageDetailViewProps<T extends UsageSubject> {
    id: string | undefined;
    backTo: string;
    pageSizeKey: string;
    load: (id: string, query: UsageEntriesQuery) => Promise<UsageDetailPage<T>>;
    // Admin sees output tokens. Users do not.
    showOutputTokens?: boolean;
    // What the subject is called in messages: "operation" or "job".
    noun: string;
    title: (subject: T) => React.ReactNode;
    // Rendered next to the title: a status badge or a link.
    titleAside?: (subject: T) => React.ReactNode;
    fields: (subject: T) => React.ReactNode;
    notice?: (subject: T) => string | null;
    // Usage entries exist only for a finished subject. Default: always.
    showUsage?: (subject: T) => boolean;
}

const BASE_SORT_KEYS = ['filename', 'name', 'type', 'input_tokens', 'input_tokens_converted', 'input_tokens_supported', 'cost', 'estimated_cost', 'billing_status', 'created_at'] as const;
const URL_SPEC = {
    sort: urlEnum(BASE_SORT_KEYS, 'created_at'),
    order: urlEnum(['asc', 'desc'] as const, 'desc'),
    page: urlPositiveInt(),
};
const ADMIN_URL_SPEC = {
    ...URL_SPEC,
    sort: urlEnum([...BASE_SORT_KEYS, 'output_tokens'] as const, 'created_at'),
};

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <div className="text-xs text-secondary uppercase tracking-wider">{label}</div>
            <div className="text-sm text-body-color mt-1">{children}</div>
        </div>
    );
}

function UsageDetailView<T extends UsageSubject>({
    id,
    backTo,
    pageSizeKey,
    load,
    showOutputTokens = false,
    noun,
    title,
    titleAside,
    fields,
    notice,
    showUsage = () => true,
}: UsageDetailViewProps<T>) {
    const [subject, setSubject] = useState<T | null>(null);
    const [entries, setEntries] = useState<UsageEntryFields[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [limit, setLimit] = usePageSize(pageSizeKey);
    const [state, setUrlState] = useUrlState(showOutputTokens ? ADMIN_URL_SPEC : URL_SPEC);
    const backHref = useBackTo(backTo);
    const offset = (state.page - 1) * limit;
    const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

    useEffect(() => {
        if (!id) return;
        // Dropped on the next request, so a slow old response cannot win.
        let current = true;
        const fetchData = async () => {
            setLoading(true);
            try {
                const page = await load(id, { limit, offset, sort: state.sort, order: state.order });
                if (!current) return;
                setSubject(page.subject);
                setEntries(page.entries);
                setTotal(page.total);
                setError(null);
            } catch (err: any) {
                if (!current) return;
                setSubject(null);
                setEntries([]);
                setTotal(0);
                setError(err?.response?.status === 404 ? `${Noun} not found.` : `Failed to load ${noun}.`);
            } finally {
                if (current) setLoading(false);
            }
        };
        fetchData();
        return () => {
            current = false;
        };
    }, [id, offset, limit, state.sort, state.order, load, noun, Noun]);

    usePageClamp({
        page: state.page,
        pageSize: limit,
        total,
        ready: !loading && !error,
        setPage: (page) => setUrlState({ page }),
    });

    const onSort = (sort: string, order: 'asc' | 'desc') =>
        setUrlState({ sort: sort as typeof state.sort, order, page: 1 });

    const usageVisible = !!subject && showUsage(subject);

    // Convert reports converted tokens and a frozen cost. Analyze reports
    // supported tokens and an estimate. The other kind is always 0, so it is hidden.
    const isConvert = subject?.operation === 'convert';
    const kindColumn = isConvert
        ? { label: 'In Tokens (Converted)', sortKey: 'input_tokens_converted', value: (entry: UsageEntryFields) => entry.input_tokens_converted }
        : { label: 'In Tokens (Supported)', sortKey: 'input_tokens_supported', value: (entry: UsageEntryFields) => entry.input_tokens_supported };
    const costColumn = isConvert
        ? { label: 'Cost', sortKey: 'cost' }
        : { label: 'Est. Cost', sortKey: 'estimated_cost' };
    // The cost is a tile, not a card field: like the entries, it exists only with usage.
    const tiles = usageVisible && subject
        ? [
              { label: isConvert ? 'Metered Entries' : 'Unmetered Entries', value: subject.unique_count.toLocaleString() },
              { label: 'Duplicate Entries', value: subject.duplicate_count.toLocaleString() },
              { label: 'In Tokens', value: subject.input_tokens_sum.toLocaleString() },
              { label: kindColumn.label, value: (isConvert ? subject.input_tokens_converted_sum : subject.input_tokens_supported_sum).toLocaleString() },
              ...(showOutputTokens ? [{ label: 'Out Tokens', value: subject.output_tokens_sum.toLocaleString() }] : []),
              { label: costLabel(subject.operation), value: isConvert ? formatCentsAsDollars(subject.cost_cents) : formatEstimateCents(subject.estimated_cost_cents) },
          ]
        : [];
    const noticeText = subject ? notice?.(subject) : null;

    if (!id) return null;

    return (
        <div>
            <Link to={backHref} className="text-sm text-secondary hover:text-body-color inline-flex items-center gap-1 mb-3">
                <ArrowLeftIcon className="w-4 h-4" />
                Back
            </Link>

            {error && <div className="bg-red-600/20 text-red-400 px-4 py-2 rounded text-sm">{error}</div>}

            {!subject ? (
                !error && <div className="text-secondary text-sm">Loading...</div>
            ) : (
                <>
                    <div className="flex items-center gap-3 mb-1">
                        <h1 className="text-2xl font-bold text-heading">{title(subject)}</h1>
                        {titleAside?.(subject)}
                    </div>
                    <div className="flex items-center gap-1 mb-4">
                        <span className="font-mono text-xs text-secondary">{subject.id}</span>
                        <CopyIconButton text={subject.id} label={`${noun} ID`} iconClassName="w-3.5 h-3.5" />
                    </div>
                    <p className="text-xs text-secondary mb-3">All dates and times are UTC.</p>

                    {noticeText && (
                        <div className="mb-4 bg-red-600/10 text-red-400 px-3 py-2 rounded text-sm">
                            {noticeText}
                        </div>
                    )}

                    <div className="bg-widget-bg rounded-lg border border-gray-700 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {fields(subject)}
                    </div>

                    {usageVisible && (
                        <>
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-4 mb-6">
                                {tiles.map((tile) => (
                                    <StatTile key={tile.label} label={tile.label} value={tile.value} />
                                ))}
                            </div>

                            <TableCard>
                                <TableHeader
                                    title="Usage Entries"
                                    pagination={{
                                        offset,
                                        limit,
                                        total,
                                        onOffsetChange: (nextOffset) => setUrlState({ page: nextOffset / limit + 1 }),
                                        onLimitChange: (size) => {
                                            setLimit(size);
                                            setUrlState({ page: 1 });
                                        },
                                    }}
                                />
                                <TableViewport className={loading ? 'opacity-50 transition-opacity' : ''}>
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-700">
                                                <SortableTh label="File" sortKey="filename" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                                <SortableTh label="Name" sortKey="name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                                <SortableTh label="Type" sortKey="type" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                                <SortableTh label="Billing" sortKey="billing_status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                                <th className={thClass}>Fingerprint</th>
                                                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                                                <SortableTh label={kindColumn.label} sortKey={kindColumn.sortKey} sort={state.sort} order={state.order} onSort={onSort} />
                                                <SortableTh label={costColumn.label} sortKey={costColumn.sortKey} sort={state.sort} order={state.order} onSort={onSort} />
                                                {showOutputTokens && (
                                                    <SortableTh label="Out Tokens" sortKey="output_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                                                )}
                                                <SortableTh label="Recorded At" sortKey="created_at" sort={state.sort} order={state.order} onSort={onSort} />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700">
                                            {entries.map((entry) => (
                                                <tr key={entry.id}>
                                                    <td className={`${tdClass} font-mono max-w-[260px] truncate`} title={entry.filename}>
                                                        {entry.filename}
                                                    </td>
                                                    <td className={`${tdClass} font-mono max-w-[260px] truncate`} title={entry.name}>
                                                        {entry.name}
                                                    </td>
                                                    <td className={tdClass}>{entry.type}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <Badge value={entry.billing_status} />
                                                    </td>
                                                    <td className={`${tdClass} text-secondary font-mono`} title={entry.fingerprint}>
                                                        {entry.fingerprint.replace('sha256:', '').slice(0, 12)}…
                                                    </td>
                                                    <td className={tdClass}>{entry.input_tokens.toLocaleString()}</td>
                                                    <td className={tdClass}>{kindColumn.value(entry).toLocaleString()}</td>
                                                    <td className={tdClass}><CliCost operation={entry.operation} cost={entry.cost_cents} estimate={entry.estimated_cost_cents} /></td>
                                                    {showOutputTokens && <td className={tdClass}>{entry.output_tokens.toLocaleString()}</td>}
                                                    <td className={tdClass}>{formatDateTimeUTC(entry.created_at)}</td>
                                                </tr>
                                            ))}
                                            {entries.length === 0 && (
                                                <EmptyRow colSpan={showOutputTokens ? 10 : 9} text={loading ? 'Loading...' : `No usage entries for this ${noun}.`} />
                                            )}
                                        </tbody>
                                    </table>
                                </TableViewport>
                            </TableCard>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

export default UsageDetailView;
